import { isExpired } from "./auth"

/**
 * Post-login destinations.
 *
 * Maker order emails link into the hub (`hub.anddine.co.uk/orders?searchTerm=<order ref>`), as
 * do file-download emails (`/files/download?queueId=…`), so a maker who is not signed in has to
 * land back on that page rather than the dashboard. `auth.global.ts` parks the intended path in
 * `?redirect=` on its way to `/login`, and `pages/login.vue` reads it back out.
 *
 * Both directions go through `safeRedirectPath` — the gate that builds the value and the page
 * that consumes it are the same function, so a new call site cannot apply a looser rule than
 * the one that produced the value.
 *
 * The value is attacker-controllable (it is a query string on a public page), so this is an
 * open-redirect boundary: anything that is not an unambiguous same-origin path falls back to
 * `DEFAULT_REDIRECT` rather than being repaired. Phishing is the reason — a login page that
 * forwards to an arbitrary host after a real authentication is a credible way to hand a maker
 * to a lookalike site.
 */

export const DEFAULT_REDIRECT = "/"

/** Longer than any real hub route; a value past this is noise, not a destination. */
const MAX_LENGTH = 512

/**
 * CR/LF and friends — never legitimate in a path, and the classic header/log injection.
 * Scanned by char code rather than a regex, which would need a `no-control-regex` suppression.
 */
const hasControlCharacter = (text: string): boolean => {
	for (let index = 0; index < text.length; index += 1) {
		const code = text.charCodeAt(index)
		if (code <= 0x1f || code === 0x7f) return true
	}
	return false
}

/**
 * The routes a signed-out visitor may open. The same list decides what is never a post-login
 * destination: landing back on `/login` with a valid token leaves the maker staring at the form
 * they just completed, since the middleware will not bounce a public route anywhere.
 */
const PUBLIC_ROUTES = ["/login", "/forgot", "/sign-up", "/create"]
const PUBLIC_ROUTE_PREFIXES = ["/verify/", "/password/"]

/**
 * vue-router matches without case or a trailing slash (`strict` and `sensitive` both default to
 * false) but keeps `to.path` as typed, so `/login/` and `/LOGIN` render the login page. Compare
 * the form the router matches on, or those slip past as ordinary private routes.
 */
const routerMatchForm = (path: string): string => path.replace(/\/+$/, "").toLowerCase() || "/"

export const isPublicRoute = (path: string): boolean => {
	const normalised = routerMatchForm(path)
	return (
		PUBLIC_ROUTES.includes(normalised) ||
		PUBLIC_ROUTE_PREFIXES.some((prefix) => normalised.startsWith(prefix))
	)
}

/**
 * `route.query.x` is `string | null | (string | null)[]` at runtime whatever TypeScript says —
 * `?redirect=&redirect=/orders/1` arrives as an array, and a valueless repeated key puts a
 * `null` in it. Taking the first non-empty string is the same rule site applies in
 * `firstQueryValue.ts`.
 */
const firstString = (value: unknown): string => {
	if (Array.isArray(value)) {
		const found = value.find((entry) => typeof entry === "string" && entry.trim() !== "")
		return typeof found === "string" ? found.trim() : ""
	}
	return typeof value === "string" ? value.trim() : ""
}

/**
 * The intended path if it is safe to navigate to, otherwise `DEFAULT_REDIRECT`.
 *
 * Accepts only a rooted, same-origin path: a single leading `/`, no backslashes (browsers
 * normalise `\` to `/`, so `/\evil.com` can resolve as the protocol-relative `//evil.com`),
 * and no scheme. A query string and hash on the target are kept, so a future
 * `/orders/123?tab=delivery` link survives.
 */
export const safeRedirectPath = (value: unknown): string => {
	const raw = firstString(value)

	if (!raw) return DEFAULT_REDIRECT
	if (raw.length > MAX_LENGTH) return DEFAULT_REDIRECT
	if (hasControlCharacter(raw)) return DEFAULT_REDIRECT
	// A real value is percent-encoded, so whitespace only arrives in a crafted link — and
	// `navigateTo` reads `/ /x` as protocol-relative and throws after the login has succeeded.
	if (/\s/.test(raw)) return DEFAULT_REDIRECT

	// Anything not starting with a single `/` is either absolute (`https://…`), scheme-relative
	// (`//evil.com`) or a non-navigational scheme (`javascript:`, `data:`).
	if (!raw.startsWith("/")) return DEFAULT_REDIRECT
	if (raw.startsWith("//")) return DEFAULT_REDIRECT
	if (raw.includes("\\")) return DEFAULT_REDIRECT
	if (raw.includes("://")) return DEFAULT_REDIRECT

	if (isPublicRoute(raw.split(/[?#]/)[0])) return DEFAULT_REDIRECT

	return raw
}

/**
 * The login URL to bounce an unauthenticated visit to, carrying where it was heading.
 *
 * Returns a bare `/login` when there is nothing worth preserving, so the common case keeps a
 * clean URL and `?redirect=/` never appears.
 */
export const loginPathFor = (fullPath: unknown): string => {
	const target = safeRedirectPath(fullPath)
	return target === DEFAULT_REDIRECT ? "/login" : `/login?redirect=${encodeURIComponent(target)}`
}

/**
 * Where an API call that came back 401/403 sends the user.
 *
 * Only a session that has actually died parks the page. The backend sends 401 for a dead token,
 * but also from the admin-only route guard (`checkIfUserAdminRouteGuard`) to a maker whose token
 * is fine, and a 403 is a permission answer: parking either would land them back on a page that
 * rejects them again, after every sign-in. A token the hub still thinks is live gets a bare
 * `/login`, as before.
 */
export const loginPathForAuthError = (
	status: number | undefined,
	fullPath: unknown,
	token: string | null | undefined
): string => (status === 401 && (!token || isExpired(token)) ? loginPathFor(fullPath) : "/login")
